import React, { useMemo, useRef } from "react";
import {
  Card,
  Descriptions,
  Button,
  Table,
  Timeline,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Divider,
  Typography,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  LeftOutlined,
  PrinterOutlined,
  DollarOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./styles.scss";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import OrderService from "services/order";
import moment from "moment";
import { CustomerText } from "components/DataTableText";
import { parseJSONSafely } from "helper/common";
import { isInsuranceItem, getPromotionInfo } from "helper/orderHelper";
import { ORDER_TYPE } from "constants/order";
import { ORDER_PAYMENT_STATUS } from "constants/order";
import { stations_location } from "constants/order";
import { PAYMENT_METHOD_LABEL } from "constants/order";
import { INSURANCE_CAR_TYPE_LABEL_BY_SUB_TYPE } from "constants/order";
import { SPECIAL_TRANSPORT_LABEL } from "constants/order";
import { getVehicleSubTypes, getVehicleSubCategories } from "constants/listSchedule";
import PrintOrderButton from "components/PrintOrderButton";
import { CardOrderSummary } from "../ListOrder/_components/OrderCards";

const ORDER_CATEGORY_GIFT = 2;

const OrderDetail = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const backTab = location.state?.backTab || "2";

  const { data: order, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => OrderService.getListById({ id }),
    enabled: !!id,
  });

  const updateOrderStatus = async (newStatus) => {
    try {
      await OrderService.updateOrder({
        id: id,
        data: {
          orderStatus: newStatus,
          note: " ",
        },
      });
      refetch();
    } catch (error) {}
  };

  const updateOrderPaymentStatus = async (newStatus) => {
    try {
      await OrderService.updateOrder({
        id: id,
        data: {
          paymentStatus: newStatus,
          note: " ",
        },
      });
      refetch();
    } catch (error) {}
  };

  const orderType = useMemo(
    () => Object.values(ORDER_TYPE).find((el) => el.value === order?.orderType),
    [order]
  );

  const paymentStatus = useMemo(
    () =>
      Object.values(ORDER_PAYMENT_STATUS).find(
        (el) => el.value === order?.paymentStatus
      ),
    [order]
  );

  const status = useMemo(
    () =>
      Object.values(stations_location).find(
        (el) => el.value === order?.orderStatus
      ),
    [order]
  );

  const vehicleSubTypeMap = useMemo(() => getVehicleSubTypes(translate), [translate]);
  const vehicleSubCategoryMap = useMemo(
    () => getVehicleSubCategories(translate),
    [translate]
  );

  // prepare products and totals for summary column
  const selectedProducts = order?.orderItems?.map((item) => ({
    productId: item.productId,
    orderQuantity: item.quantity || item.orderQuantity || 0,
    product: {
      productId: item.productId,
      productName: item.orderItemName || item.orderItemName || "",
      price: item.productPrice || item.payAmount || 0,
    },
  })) || [];

  const subtotal = order?.subTotalAmount || order?.subtotal || 0;
  const tax = order?.taxAmount || order?.tax || 0;
  const fee = order?.feeAmount || 0;
  const total = order?.totalAmount || order?.payAmount || 0;
  const discountAmount = Number(order?.discountAmount || 0);

  const insuranceDetailList = useMemo(() => {
    const orderItems = Array.isArray(order?.orderItems) ? order.orderItems : [];
    const insuranceItems = orderItems
      .map((item) => {
        if (!isInsuranceItem(order, item)) return null;

        const orderItemVehicle = parseJSONSafely(item?.orderItemVehicle);
        const orderItemUser = parseJSONSafely(item?.orderItemUser);
        const orderItemOtherData = parseJSONSafely(item?.orderItemOtherData);
        const vehicleSubType = Number(orderItemVehicle?.vehicleSubType);
        const vehicleSubCategory = Number(orderItemVehicle?.vehicleSubCategory);

        return {
          key: item?.orderItemId,
          provider: item?.productProvider,
          vehicleType: orderItemVehicle?.vehicleType,
          vehicleSubType: orderItemVehicle?.vehicleSubType,
          vehicleSubCategory: orderItemVehicle?.vehicleSubCategory,
          vehicleTypeDisplay:
            INSURANCE_CAR_TYPE_LABEL_BY_SUB_TYPE?.[vehicleSubType] ||
            vehicleSubTypeMap?.[vehicleSubType] ||
            vehicleSubCategoryMap?.[vehicleSubCategory] ||
            orderItemVehicle?.vehicleSubType ||
            orderItemVehicle?.vehicleSubCategory ||
            orderItemVehicle?.vehicleType ||
            "",
          vehicleForBusiness: orderItemVehicle?.vehicleForBusiness,
          vehicleIdentity: orderItemVehicle?.vehicleIdentity,
          vehicleBrand: orderItemVehicle?.vehicleBrandName,
          yearManufacture: orderItemVehicle?.yearManufacture,
          vehicleSeatsLimit: orderItemVehicle?.vehicleSeatsLimit,
          isTaxi: orderItemVehicle?.isTaxi,
          isPracticedCar: orderItemVehicle?.isPracticedCar,
          vehicleTotalWeight: orderItemVehicle?.vehicleTotalWeight,
          specialTransport: orderItemVehicle?.specialTransport,
          chassisNumber: orderItemVehicle?.chassisNumber,
          engineNumber: orderItemVehicle?.engineNumber,
          ownerName: orderItemUser?.firstName,
          ownerPhone: orderItemUser?.phoneNumber,
          ownerIdentity: orderItemUser?.appUserIdentity,
          ownerAddress: orderItemUser?.userHomeAddress,
          ownerEmail: orderItemUser?.email,
          insuranceDuration: orderItemOtherData?.insuranceDuration,
          insuranceStartDate: orderItemOtherData?.insuranceStartDate,
          insuranceEndDate: orderItemOtherData?.insuranceEndDate,
          itemPayAmount: item?.payAmount,
          itemDiscountAmount: item?.discountAmount,
        };
      })
      .filter(Boolean);

    const toDate = (value) =>
      value ? moment(value, "DD/MM/YYYY", true) : null;
    const normalize = (value) =>
      value === undefined || value === null ? "" : String(value);
    const groups = new Map();

    // Gộp các item trùng thông tin xe/chủ xe để tránh hiển thị lặp, chỉ giữ khoảng thời gian BH rộng nhất.
    insuranceItems.forEach((item) => {
      const groupKey = [
        item.provider,
        item.vehicleType,
        item.vehicleSubType,
        item.vehicleSubCategory,
        item.vehicleTypeDisplay,
        item.vehicleForBusiness,
        item.vehicleIdentity,
        item.vehicleBrand,
        item.yearManufacture,
        item.vehicleSeatsLimit,
        item.isTaxi,
        item.isPracticedCar,
        item.vehicleTotalWeight,
        item.specialTransport,
        item.chassisNumber,
        item.engineNumber,
        item.ownerName,
        item.ownerPhone,
        item.ownerIdentity,
        item.ownerAddress,
        item.ownerEmail,
        item.insuranceDuration,
      ]
        .map(normalize)
        .join("|");

      const startDate = toDate(item.insuranceStartDate);
      const endDate = toDate(item.insuranceEndDate);
      const current = groups.get(groupKey);

      if (!current) {
        groups.set(groupKey, {
          ...item,
          insuranceStartDate:
            startDate && startDate.isValid()
              ? startDate.format("DD/MM/YYYY")
              : item.insuranceStartDate || "",
          insuranceEndDate:
            endDate && endDate.isValid()
              ? endDate.format("DD/MM/YYYY")
              : item.insuranceEndDate || "",
        });
        return;
      }

      const currentStart = toDate(current.insuranceStartDate);
      const currentEnd = toDate(current.insuranceEndDate);

      if (
        startDate &&
        startDate.isValid() &&
        (!currentStart || !currentStart.isValid() || startDate.isBefore(currentStart))
      ) {
        current.insuranceStartDate = startDate.format("DD/MM/YYYY");
      }

      if (
        endDate &&
        endDate.isValid() &&
        (!currentEnd || !currentEnd.isValid() || endDate.isAfter(currentEnd))
      ) {
        current.insuranceEndDate = endDate.format("DD/MM/YYYY");
      }

      if (!current.key && item?.key) current.key = item.key;
    });

    return Array.from(groups.values());
  }, [order, vehicleSubCategoryMap, vehicleSubTypeMap]);

  const promotionInfo = useMemo(() => {
    return getPromotionInfo(order, discountAmount);
  }, [discountAmount, order]);
  const customerEmail = order?.email || insuranceDetailList?.[0]?.ownerEmail || "-";
  const hasDisplayValue = (value) =>
    !(value === undefined || value === null || value === "");
  const getYesNoDisplay = (value) => {
    if (!hasDisplayValue(value)) return "";
    return Number(value) === 1 ? "Có" : "Không";
  };
  const VEHICLE_ID_BY_SUB_TYPE = {
    1: 1,
    2: 2,
    10: 10,
    11: 2,
    12: 12,
    14: 14,
  };
  const getVehicleIdBySubType = (value, fallbackValue) => {
    const normalizedSubType = Number(value);
    const mappedVehicleId = VEHICLE_ID_BY_SUB_TYPE[normalizedSubType];

    if (mappedVehicleId !== undefined) return mappedVehicleId;

    const fallback = Number(fallbackValue);
    return Number.isNaN(fallback) ? undefined : fallback;
  };
  const getVehicleFieldVisibility = (vehicleSubType, vehicleForBusiness, vehicleType) => {
    const normalizedVehicleId = getVehicleIdBySubType(vehicleSubType, vehicleType);
    const normalizedVehicleForBusiness = Number(vehicleForBusiness);
    const isPassengerVehicle = [1, 2, 14].includes(normalizedVehicleId);

    return {
      showIsTaxi: isPassengerVehicle && normalizedVehicleForBusiness === 1,
      showIsPracticedCar:
        (isPassengerVehicle && normalizedVehicleForBusiness === 0) ||
        (normalizedVehicleId === 12 && normalizedVehicleForBusiness !== 1),
      showVehicleTotalWeight: normalizedVehicleId === 12,
      showSpecialTransport: normalizedVehicleId === 10,
    };
  };
  const renderInsuranceInfoRow = (label, value) => {
    if (!hasDisplayValue(value)) return null;
    return (
      <div className="insurance-extra-row">
        <div className="insurance-extra-label">{label}</div>
        <div className="insurance-extra-value">{value}</div>
      </div>
    );
  };
  const formatInsuranceDate = (value) => {
    if (!hasDisplayValue(value)) return "";
    const parsedDate = moment(value, "DD/MM/YYYY", true);
    return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : String(value);
  };

  const latestUpdated = order?.updatedAt || order?.createdAt;
  // Resolve payment method display: try name/refer/provider but map numeric ids (even if strings)
  const resolvePaymentMethodDisplay = (ord) => {
    if (!ord) return "-";
    const fields = [ord.paymentMethodName, ord.orderPaymentProvider, ord.paymentMethodReferName, ord.paymentMethodId];
    for (let f of fields) {
      if (f === null || f === undefined) continue;
      // if numeric or numeric string, map via PAYMENT_METHOD_LABEL
      const n = Number(f);
      if (!Number.isNaN(n) && PAYMENT_METHOD_LABEL[n]) return PAYMENT_METHOD_LABEL[n];
      // also handle strings that may contain an id like "method_3" or just return non-empty strings
      if (typeof f === 'string') {
        const m = f.match(/(\d+)/);
        if (m && PAYMENT_METHOD_LABEL[Number(m[1])]) return PAYMENT_METHOD_LABEL[Number(m[1])];
        if (f.trim() !== '') return f;
      }
      // if value is numeric but not in mapping, show as #id
      if (typeof f === 'number') return `#${f}`;
    }
    return "-";
  };

  const paymentMethodDisplay = resolvePaymentMethodDisplay(order);
  const isGiftOrder = order?.orderCategory === ORDER_CATEGORY_GIFT;

  return (
    <div className="order-detail-container">
      <Button
        type="text"
        icon={<LeftOutlined />}
        onClick={() => history.push({ pathname: "/orders", search: `?tab=${backTab}` })}
        className="back-button"
      >
        {translate("back")}
      </Button>

      <div className="order-content">
        {/* Left column: single bordered card containing header + product list + totals */}
        <div className="order-left">
          <div className="co-card-wrap">
            <div className="co-card-title">{translate("order.info")}</div>
            <div className="co-card-content">
              <div className="order-info">
                <div className="order-summary">
                  <div className="op-order-info-row">
                    <div className="order-id">{order?.orderCode ? `#${order.orderCode}` : (order?.orderId ? `#${order.orderId}` : "-")}</div>
                    <div>
                      <Tag className={`status-badge ${status?.value ? `status-${status.value}` : ''}`}>
                        {translate(status?.label) || ""}
                      </Tag>
                    </div>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <Typography.Text type="secondary">{moment(latestUpdated).format("DD/MM/YYYY HH:mm:ss")}</Typography.Text>
                  </div>

                  {order?.paymentStatus === 'Success' && !isGiftOrder && (
                    <div className="op-order-info-row" style={{ marginBottom: 12 }}>
                      <div className="op-label">{translate('order.paymentMethod')}</div>
                      <div className="op-value">{(order?.paymentMethodId !== undefined && order?.paymentMethodId !== null) ? (PAYMENT_METHOD_LABEL[Number(order.paymentMethodId)] || `#${order.paymentMethodId}`) : '-'}</div>
                    </div>
                  )}

                  {/* <div className="op-order-info-row" style={{ marginBottom: 12 }}>
                    <div className="op-label">{translate('order.orderType')}</div>
                    <div className="op-value">{translate(orderType?.label) || '-'}</div>
                  </div> */}

                  <Divider className="co-divider" />
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <CardOrderSummary
                  noWrapper
                  showPaymentHeader={false}
                  selectedProducts={selectedProducts}
                  subtotal={subtotal}
                  tax={tax}
                  fee={fee}
                  total={total}
                  discountAmount={discountAmount}
                  showActions={false}
                  isPaymentPage={false}
                  isDetailPage={true}
                  orderId={order?.orderId}
                  createdAt={order?.createdAt ? new Date(order.createdAt).toLocaleString('vi-VN') : ''}
                  customerData={{ firstName: order?.firstName, phoneNumber: order?.phoneNumber, userHomeAddress: order?.userHomeAddress || order?.address, appUserIdentity: order?.appUserIdentity }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Customer info */}
        <div className="order-right">
          <div className="co-card-wrap">
            <div className="co-card-title">{translate('order.customerInfo')}</div>
            <div className="co-card-content">
              <div className="op-customer-info">
                <div className="op-customer-name">{order?.firstName}</div>
                <div className="op-customer-phone">{order?.phoneNumber}</div>
                {order?.appUserIdentity && (
                  <div className="op-customer-phone">
                    {/^\d{13}$/.test(order.appUserIdentity)
                      ? `${order.appUserIdentity.substring(0, 10)}-${order.appUserIdentity.substring(10)}`
                      : order.appUserIdentity}
                  </div>
                )}
                <div className="op-customer-phone">{customerEmail}</div>
                {order?.userHomeAddress && <div className="op-customer-address">{order.userHomeAddress}</div>}
              </div>

              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                <PrintOrderButton
                  orderId={order?.orderId}
                  showText={true}
                  label={'Xuất hóa đơn điện tử (MMT)'}
                  buttonType="default"
                  className="order-print-btn"
                />
                {order?.paymentStatus !== "Success" && order?.orderStatus !== 20 && !isGiftOrder && (
                  <Button 
                    type="default" 
                    icon={<DollarOutlined />} 
                    className="order-pay-btn" 
                    onClick={() => {
                      localStorage.setItem('pendingOrderId', order?.orderId);
                      history.push('/orders?tab=1&payment=true');
                    }}
                  >
                    {translate('order.pay')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        {!isGiftOrder && insuranceDetailList.length > 0 && (
          <div className="order-third">
            <div className="co-card-wrap">
              <div className="co-card-title">Ghi chú đơn hàng</div>
              <div className="co-card-content">
                {insuranceDetailList.map((item, index) => (
                  <div className="insurance-extra-item" key={item.key || index}>
                    {(() => {
                      const vehicleFieldVisibility = getVehicleFieldVisibility(
                        item.vehicleSubType,
                        item.vehicleForBusiness,
                        item.vehicleType
                      );

                      return (
                        <>
                          {renderInsuranceInfoRow("Biển số xe", item.vehicleIdentity)}
                          {renderInsuranceInfoRow("Nhãn hiệu", item.vehicleBrand)}
                          {renderInsuranceInfoRow("Số máy", item.engineNumber)}
                          {renderInsuranceInfoRow("Số khung", item.chassisNumber)}
                          {renderInsuranceInfoRow("Năm SX", item.yearManufacture)}
                          {renderInsuranceInfoRow(
                            "Thời gian bảo hiểm",
                            item.insuranceStartDate || item.insuranceEndDate
                              ? `${item.insuranceStartDate ? formatInsuranceDate(item.insuranceStartDate) : ""}${
                                  item.insuranceStartDate && item.insuranceEndDate ? " - " : ""
                                }${item.insuranceEndDate ? formatInsuranceDate(item.insuranceEndDate) : ""}`
                              : ""
                          )}
                          {renderInsuranceInfoRow(
                            "Kinh doanh",
                            hasDisplayValue(item.vehicleForBusiness)
                              ? Number(item.vehicleForBusiness) === 1
                                ? "Có kinh doanh"
                                : "Không kinh doanh"
                              : ""
                          )}
                          {vehicleFieldVisibility.showIsTaxi &&
                            renderInsuranceInfoRow("Xe taxi", getYesNoDisplay(item.isTaxi))}
                          {vehicleFieldVisibility.showIsPracticedCar &&
                            renderInsuranceInfoRow(
                              "Xe tập lái",
                              getYesNoDisplay(item.isPracticedCar)
                            )}
                          {vehicleFieldVisibility.showVehicleTotalWeight &&
                            renderInsuranceInfoRow(
                              "Trọng tải",
                              hasDisplayValue(item.vehicleTotalWeight)
                                ? `${item.vehicleTotalWeight} tấn`
                                : ""
                            )}
                          {vehicleFieldVisibility.showSpecialTransport &&
                            renderInsuranceInfoRow(
                              "Loại vận chuyển",
                              SPECIAL_TRANSPORT_LABEL?.[Number(item.specialTransport)] || ""
                            )}
                          {renderInsuranceInfoRow("Loại xe", item.vehicleTypeDisplay)}
                          {renderInsuranceInfoRow("Số chỗ", item.vehicleSeatsLimit)}
                          {renderInsuranceInfoRow("Tên nhà bảo hiểm", item.provider)}
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default OrderDetail;
